<template>
  <v-form-group class="c-widget-input-symbol" :label="label" label-cols="auto">
    <v-form-tags
      addOnlyFromAutocomplete
      :autocompleteItems="autocompleteItems"
      :description="description"
      :disabled="disabled"
      :maxTags="maxTags"
      :multicolor="multicolor"
      :placeholder="placeholder"
      :value="tags"
      @input="onTagsUpdate"
      @inputText="currentInput = $event"
      detached
      :detachedClass="detachedClasses"
    >
      <template slot="autocomplete-item" slot-scope="props">
        <div @click="onAutocompleteItemSelect(props)">
          <span class="ti-item--flag">
            <v-flag :countryCode="props.item.country"
              ><slot>{{ $t(`white-label.countries.${props.item.country}`) }}</slot></v-flag
            >
          </span>
          <span class="ti-item--ticker">{{ props.item.text }}</span>
          <span class="ti-item--desc">{{ props.item.description }}</span>
        </div>
      </template>
    </v-form-tags>
  </v-form-group>
</template>

<script>
import { API, toSymbol } from '@/state/api';
import { debounce, xorWith, isEmpty, isEqual } from 'lodash-es';

const name = 'c-widget-input-symbol';
export default {
  name,
  props: {
    autocompleteInstruments: {
      type: Array,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    description: {
      type: String
    },
    multicolor: {
      type: Boolean,
      default: false
    },
    // Tag Input
    value: {
      required: true,
      type: Array
    },
    maxTags: {
      type: Number
    },
    placeholder: {
      type: String
    },
    disabled: {
      type: Boolean
    },
    detached: {
      type: Boolean
    },
    detachedClass: {
      type: [String, Array],
      default: ''
    }
  },
  data() {
    return {
      currentInput: '',
      autocompleteItems: [],
      exchanges: 'TOR,CVE,NLB,CNX,MOE,ALP,NYQ,NYS,NAQ,NAS,NSQ,NSM,NMQ,NMS,ASQ,BTQ,ASE,PCQ,PSE,ADC,NYM,OPQ,MXP,IOM',
      countries: 'CA,US',
      symbolTickerMap: {}
    };
  },
  computed: {
    tags() {
      return this.value.map(ric => ({
        ric,
        text: this.symbolTickerMap[ric] || this.fetchTicker(ric)
      }));
    },
    detachedClasses() {
      if (this.detached) {
        return (this.detachedClass
          ? Array.isArray(this.detachedClass)
            ? this.detachedClass
            : [this.detachedClass]
          : []
        ).concat('c-widget-input-symbol');
      }
    }
  },
  watch: {
    value(newVal, oldVal) {
      // Emit input for maxTags = 1, if value changes outside of component
      if (this.maxTags <= 1 && this.currentInput !== newVal[0] && newVal.length === oldVal.length) {
        if (!isEmpty(xorWith(newVal, oldVal, isEqual))) {
          this.currentInput = newVal;
          this.$emit('input', newVal);
        }
      }
    },
    currentInput() {
      this.searchRics();
    }
  },
  methods: {
    fetchTicker(ric) {
      toSymbol(ric).then(res => this.$set(this.symbolTickerMap, ric, res[0].replace(/:.*/, '')));
      // put a truncated ric in place of unresolved ticker for now
      return ric.replace(/([^#.])\..+/, '$1');
    },
    onAutocompleteItemSelect(ti) {
      this.$set(this.symbolTickerMap, ti.item.ric, ti.item.text);
      ti.performAdd(ti.item);
    },
    onTagsUpdate(tags) {
      const newRics = tags.map(tag => tag.ric);
      if (this.value.join() !== newRics.join()) {
        this.$emit('input', newRics);
      }
    },
    searchRics: debounce(function() {
      if (!this.currentInput) {
        this.autocompleteItems = [];
        return;
      }
      let uri = `/product/td/quicksearch?text=${encodeURIComponent(this.currentInput)}&exchanges=${
        this.exchanges
      }&countries=${this.countries}`;

      API.Rest.get(uri)
        .catch(error => {
          console.error(error);
        })
        .then(result => {
          this.autocompleteItems = result.data.data
            .filter(d =>
              this.autocompleteInstruments !== undefined ? this.autocompleteInstruments.includes(d.type) : d
            )
            .map(d => ({ country: d.country, text: d.ticker || d.symbol, description: d.displayName, ric: d.symbol }));
        });
    }, 250)
  }
};
</script>

<style lang="scss">
@import '~@/styles/components/custom/c-widget-input-symbol';
</style>
